import apisauce from 'apisauce';
import moment from 'moment';
import { config } from "../constants/config";

const { baseURL } = config;

let gtfsChangesEtagCache = {};

const getUrl = () => baseURL;

const customCaching = (etag) => ({
  headers: {
    "Cache-Control": "no-store",
    "If-None-Match": etag,
  },
});

export const processApiResponse = ({ data, ok, problem }) => {
  if (ok) {
    if (data?.status === 'OK' || !data?.status) {
      return Promise.resolve(data?.response ?? data);
    }
    return Promise.reject(new Error('No results found'));
  }
  return Promise.reject(new Error(problem || 'Impossible to query the API'));
};

const create = (cancelToken) => {
  const api = apisauce.create({
    // base URL is read from the "constructor"
    baseURL,
    cancelToken,
    // 60 second timeout...
    timeout: 60000,
  });

  api.addRequestTransform((request) => {
    request.headers['Ocp-Apim-Subscription-Key'] = process.env.REACT_APP_AT_API_KEY;
  });

  /**
   * Public
   */
  const getRealtimeTripInfo = (...tripIds) => api.get(
    `realtime/legacy/`,
    { tripid: tripIds.join(',') }
  ).then(processApiResponse);

  /**
   * Service Info
   */
  const getChanges = async (stop_id, date) => {
    const etagCacheKey = `getChanges_${stop_id}_${date}`;
    const cachedEtagData = gtfsChangesEtagCache[etagCacheKey];

    const { data, headers: { etag }, ok, problem, status } = await api.get(
      `serviceinfo/v2/gtfs-changes/${date}/trips`,
      { stop_id },
      customCaching(cachedEtagData?.etag)
    )

    if (ok || [200, 304].includes(status)) {
      if (!data && etag === cachedEtagData?.etag && cachedEtagData?.data) {
        return cachedEtagData.data;
      }

      if (data && etag) {
        gtfsChangesEtagCache[etagCacheKey] = { data, etag };
      }

      return data;
    } else {
      throw new Error(problem ?? 'Impossible to query the API');
    }
  };
  const getDepartures = stopCode => api.get(
    `serviceinfo/v1/departures/${stopCode}`,
    { scope: 'tripsData' }
  ).then(processApiResponse);
  const getVehiclesByTripId = (tripId) => {
    if (!tripId) {
      return Promise.reject(new Error('No trip id provided'));
    }

    return api.get(
      `serviceinfo/v1/vehicles/byTripId/${encodeURIComponent(tripId)}`,
      { include_filtered_routes: true }
    ).then(processApiResponse);
  };
  const getOperationalChanges = (stopCode, options) => api.get(
    `serviceinfo/v1/operationalChanges/${stopCode}`,
    { scope: 'tripsData', ...options }
  ).then(processApiResponse);

  /**
   * GTFS
   */
  const getRouteById = routeId => api.get(`gtfs/legacy/routes/${routeId}`).then(processApiResponse);
  const getRoutesByStop = stopId => api.get(
    `gtfs/legacy/routes/stopId/${stopId}`,
    { pickup_types: 0 }
  ).then(processApiResponse);
  const getStopByCode = stopCode => api.get(
    `gtfs/legacy/stops/code/${stopCode}`,
    { schedule_date: moment().format('YYYY-MM-DD') }
  ).then(processApiResponse);
  const getStops = () => api.get(
    `gtfs/legacy/stops`,
    { schedule_date: moment().format('YYYY-MM-DD') }
  ).then(processApiResponse);
  const getStopInfoByCode = (stopCode, options) => {
    const queryParams = options?.startDate ? `?start_date=${options.startDate}&offset_hours=2` : '';
    const requestUrl = `gtfs/legacy/stops/stopinfo/${stopCode}${queryParams}`;
    return api.get(requestUrl).then(processApiResponse);
  };

  return {
    getChanges,
    getDepartures,
    getVehiclesByTripId,
    getOperationalChanges,
    getRealtimeTripInfo,
    getRouteById,
    getRoutesByStop,
    getStopByCode,
    getStopInfoByCode,
    getStops,
  };
};

const instance = create();
const getInstance = () => instance;

export default {
  create,
  getInstance,
  getUrl,
};
